/* eslint-disable jira/naming/disallow-filenames */

import React from 'react';
import type SystemDashboardEditType from '@jira/spa-apps__dashboard-system-dashboard-edit/src/SystemDashboard.tsx';
import {
	dashboardConfigAndWRMResource,
	dashboardAMDModulesResource,
	dashboardReactGadgetsResource,
	dashboardForgeResource,
} from '@jira/router-resources__dashboard/src/dashboardConfigAndWRMResource.tsx';
import type DashboardViewEmbedType from '@jira/global-pages__dashboard-view-embed/src/index.tsx';
import type DashboardViewType from '@jira/global-pages__dashboard-view/src/index.tsx';
import { markAsProductBundleReady } from '@jira/platform__spa/src/services/mark-as-product-bundle-ready/index.tsx';
import { LazyPage } from '@jira/platform__spa/src/view/lazy-page/index.tsx';
import type BackgroundScripts from '@jira/global-pages__dashboard-background-scripts/src/ui/index.tsx';
import type {
	DashboardConfigAndWRMResourceData,
	DashboardAMDModulesResourceData,
	DashboardForgeResourceData,
} from '@jira/global-pages__dashboard-common/src/types.tsx';
import type { DashboardReactGadgetsResourceData } from '@jira/global-pages__dashboard-react-gadgets-definition/src/main.tsx';
import { lazyForPaint, type LazyComponent } from '@atlassian/react-loosely-lazy';
import type { RouteResource } from '@atlassian/react-resource-router';

export const dashboardResources = (): (
	| RouteResource<DashboardConfigAndWRMResourceData[]>
	| RouteResource<DashboardAMDModulesResourceData>
	| RouteResource<DashboardReactGadgetsResourceData>
	| RouteResource<DashboardForgeResourceData>
)[] => [
	dashboardConfigAndWRMResource,
	dashboardAMDModulesResource,
	dashboardReactGadgetsResource,
	dashboardForgeResource,
];

export const LazyDashboardView: LazyComponent<() => React.JSX.Element> = lazyForPaint<
	typeof DashboardViewType
>(() =>
	markAsProductBundleReady(
		// eslint-disable-next-line jira/import/file-extension-required
		import(
			/* webpackChunkName: "async-dashboard-view", jiraSpaEntry: "async-dashboard-view" */ '@jira/global-pages__dashboard-view'
		),
	),
);

export const LazyDashboardViewEmbed: LazyComponent<() => React.JSX.Element> = lazyForPaint<
	typeof DashboardViewEmbedType
>(() =>
	markAsProductBundleReady(
		// eslint-disable-next-line jira/import/file-extension-required
		import(
			/* webpackChunkName: "async-dashboard-view-embed", jiraSpaEntry: "async-dashboard-view-embed" */ '@jira/global-pages__dashboard-view-embed'
		),
	),
);

export const LazySystemDashboardEdit: LazyComponent<typeof SystemDashboardEditType> = lazyForPaint<
	typeof SystemDashboardEditType
>(() =>
	markAsProductBundleReady(
		import(
			/* webpackChunkName: "async-system-dashboard-edit", jiraSpaEntry: "async-system-dashboard-edit" */ '@jira/spa-apps__dashboard-system-dashboard-edit/src/SystemDashboard.tsx'
		),
	),
);

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
export const LazyForPaintBackgroundScripts: LazyComponent<() => React.JSX.Element> = lazyForPaint<
	typeof BackgroundScripts
>(
	() =>
		import(
			/* webpackChunkName: "async-jira-dashboard-background-scripts", jiraSpaEntry: "async-jira-dashboard-background-scripts" */ '@jira/global-pages__dashboard-background-scripts/src/ui/index.tsx'
		),
	{ ssr: false },
);

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
export const LazyForPaintConnectJsApi: LazyComponent<any> = lazyForPaint<any>( // oxlint-disable-line @typescript-eslint/no-explicit-any
	() =>
		import(
			/* webpackChunkName: "async-connect-js-api", jiraSpaEntry: "async-connect-js-api" */ '@jira/platform__connect/src/loader.tsx'
		),
	{ ssr: false },
);

export const DashboardViewEmbedPage = (): React.JSX.Element => (
	<LazyPage Page={LazyDashboardViewEmbed} pageId="dashboard" shouldShowSpinner />
);

export const SystemDashboardEditPage = (): React.JSX.Element => (
	<LazyPage
		Page={LazySystemDashboardEdit}
		pageId="system-dashboard-edit"
		shouldShowSpinner={false}
	/>
);
